import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ExportButtons from "../components/ExportButtons";

const REPORTS = [
  { key: "register", label: "Complete Asset Register" },
  { key: "units", label: "QR/Barcode Asset Register (Tagged Units)" },
  { key: "ledger", label: "Consumable & Bulk Stock Ledger" },
  { key: "purchases", label: "Purchases & Vendor History" },
  { key: "transfers", label: "Assignment & Transfer History" },
  { key: "maintenance", label: "Maintenance & Repair History" },
  { key: "incidents", label: "Missing, Damaged & Disposal Register" },
  { key: "warranty", label: "Warranty & AMC Due Register" },
  { key: "room-status", label: "Room-wise Asset & Amenity Status" },
];

export default function AssetReports() {
  const [report, setReport] = useState("register");
  const [rows, setRows] = useState([]);
  const [roomStatus, setRoomStatus] = useState([]);

  useEffect(() => {
    if (report === "room-status") {
      api.get("/assets/reports/room-status").then(({ data }) => setRoomStatus(data));
    } else if (report === "register" || report === "units") {
      api.get("/assets/reports/register").then(({ data }) => setRows(report === "units" ? data.units : data.items));
    } else if (report === "ledger") {
      api.get("/assets/reports/ledger").then(({ data }) => setRows(data));
    } else {
      const path = { purchases: "/assets/purchases", transfers: "/assets/transfers", maintenance: "/assets/maintenance", incidents: "/assets/incidents", warranty: "/assets/units" }[report];
      api.get(path).then(({ data }) => setRows(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const exportable = report !== "room-status";

  return (
    <Layout>
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <h1 className="section-title">Asset Reports & Audit History</h1>
        {exportable && <ExportButtons endpoint={`/assets/export/${report}`} filenameBase={`asset_${report}`} />}
      </div>
      <AssetNav />
      <Card className="p-4 mb-4 max-w-md no-print">
        <Select value={report} onValueChange={setReport}>
          <SelectTrigger className="h-11" data-testid="report-select"><SelectValue /></SelectTrigger>
          <SelectContent>{REPORTS.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
      </Card>

      {report === "room-status" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {roomStatus.map(r => (
            <Card className="p-4" key={r.room_id}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium">Room {r.room}</span>
                <Badge variant="outline" className="text-xs">{r.status}</Badge>
                {r.asset_room_type ? <Badge className="bg-slate-900 text-white text-xs">{r.asset_room_type}</Badge> : <Badge className="bg-amber-100 border text-amber-800 text-xs">Unmapped</Badge>}
                {r.tagged_units > 0 && <Badge variant="outline" className="text-xs">{r.tagged_units} tagged</Badge>}
              </div>
              {r.lines.length ? r.lines.map((l, i) => (
                <div key={`${l.item || "i"}-${i}`} className="flex justify-between text-sm border-b border-slate-100 py-1"><span>{l.item}</span><span className="font-semibold">{l.qty}</span></div>
              )) : <div className="text-xs text-slate-400">No amenity stock recorded</div>}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-0 overflow-x-auto">
          <table className="vr-table" data-testid="report-table">
            {rows.length > 0 && (
              <thead><tr>{Object.keys(rows[0]).filter(k => !["id", "item_id", "lines", "history", "investigation_notes", "warranty", "amc", "txn_ids", "attachments"].includes(k)).slice(0, 10).map(k => <th key={k}>{k.replace(/_/g, " ")}</th>)}</tr></thead>
            )}
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i}>
                  {Object.keys(rows[0]).filter(k => !["id", "item_id", "lines", "history", "investigation_notes", "warranty", "amc", "txn_ids", "attachments"].includes(k)).slice(0, 10).map(k => (
                    <td key={k} className="text-xs max-w-[160px] truncate">{typeof r[k] === "object" && r[k] !== null ? JSON.stringify(r[k]).slice(0, 40) : String(r[k] ?? "—")}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No records yet.</div>}
        </Card>
      )}
    </Layout>
  );
}
